import { useScrollReveal } from '@/hooks/useScrollReveal';

const commitments = [
  {
    icon: 'ri-recycle-line',
    title: 'Waste Reduction at Source',
    desc: 'By freezing produce at peak quality and connecting it to demand across the year, we prevent the post-harvest losses that claim nearly 16% of India\'s perishable food output. Better cold chain equals less waste.',
    stat: '16%',
    statLabel: 'of perishable food lost post-harvest in India',
    color: 'primary',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Energy-Efficient Warehousing',
    desc: 'We partner only with warehouses that meet modern energy-efficiency standards. LED lighting, variable-speed compressors, and smart thermal curtains are minimum expectations — not nice-to-haves.',
    stat: '30%',
    statLabel: 'lower energy use vs. legacy cold stores',
    color: 'accent',
  },
  {
    icon: 'ri-route-line',
    title: 'Optimised Transport Routes',
    desc: 'Our route-matching algorithm reduces empty reefer miles by consolidating loads and backhauling. Fewer half-empty trucks on the road means lower diesel burn and fewer emissions per tonne moved.',
    stat: '25%',
    statLabel: 'reduction in empty miles through load matching',
    color: 'secondary',
  },
  {
    icon: 'ri-drop-line',
    title: 'Water & Resource Stewardship',
    desc: 'Processing partners in our network follow water-recycling protocols and by-product valorisation. Fish scales become collagen. Vegetable trim becomes compost or animal feed. Nothing useful leaves the system.',
    stat: 'Zero',
    statLabel: 'tolerance for unregulated effluent discharge',
    color: 'primary',
  },
  {
    icon: 'ri-plant-line',
    title: 'Sustainable Packaging Transition',
    desc: 'We are working with our retail and wholesale partners to phase out single-use plastics in frozen food delivery. Recyclable insulation boxes, paper-based cushioning, and compostable wraps are the new standard.',
    stat: '2027',
    statLabel: 'target for 100% recyclable retail packaging',
    color: 'accent',
  },
  {
    icon: 'ri-community-line',
    title: 'Community Cold-Chain Access',
    desc: 'Small producers in coastal and rural India often lack freezing capacity within 100 km. We are building a network of micro-cold stores and mobile blast freezers to bring preservation within reach of every community.',
    stat: '100km',
    statLabel: 'maximum distance from freeze capacity — and shrinking',
    color: 'secondary',
  },
];

export default function CommitmentSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="commitments" className="py-16 md:py-20 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100/70 border border-secondary-200/50 mb-4">
            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wider">Our Commitments</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            Sustainability Is How We Operate.
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Not a separate department. Not a marketing line. These are the principles baked into how Freshhlock designs warehouses, routes vehicles, and partners with processors every single day.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {commitments.map((item) => {
            const bgClass = item.color === 'primary'
              ? 'bg-primary-50 border-primary-200/50 hover:border-primary-300/70'
              : item.color === 'accent'
                ? 'bg-accent-50/50 border-accent-200/50 hover:border-accent-300/70'
                : 'bg-secondary-50/50 border-secondary-200/50 hover:border-secondary-300/70';

            const iconBg = item.color === 'primary'
              ? 'bg-primary-100 text-primary-600'
              : item.color === 'accent'
                ? 'bg-accent-100 text-accent-600'
                : 'bg-secondary-100 text-secondary-600';

            const statColor = item.color === 'primary'
              ? 'text-primary-600'
              : item.color === 'accent'
                ? 'text-accent-600'
                : 'text-secondary-600';

            return (
              <div key={item.title} className={`group rounded-2xl border p-6 transition-all shadow-sm hover:shadow-md ${bgClass} card-animate`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${iconBg} animate-icon-float icon-hover-pop`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-5">
                  {item.desc}
                </p>
                <div className="pt-4 border-t border-background-200/60">
                  <div className={`font-heading text-2xl font-bold ${statColor} mb-0.5`}>
                    {item.stat}
                  </div>
                  <div className="text-xs text-foreground-400">
                    {item.statLabel}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}